/** Cookie name: first-touch marketing UTMs (see packages/marketing/docs/09-event-taxonomy.md). */
export const WSA_ATTRIBUTION_COOKIE = "wsa_attribution"

/** 90 days per Phase A plan (PR #316). */
export const WSA_ATTRIBUTION_MAX_AGE_SECONDS = 60 * 60 * 24 * 90

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const

/** Bound query-string UTM length before persisting to cookies or event props. */
export const MAX_UTM_VALUE_LENGTH = 256

export type WsaUtmKey = (typeof UTM_KEYS)[number]

export type WsaAttributionPayload = Partial<Record<WsaUtmKey, string>>

export type MarketingChannelGroup =
  | "ai_referral"
  | "organic_search"
  | "paid_or_campaign"
  | "referral"
  | "direct"

export type MarketingChannelInput = {
  utmMedium?: string | null
  utmSource?: string | null
  utmCampaign?: string | null
  referrer?: string | null
}

const AI_REFERRER_HOSTS = new Set([
  "anthropic.com",
  "bard.google.com",
  "chat.openai.com",
  "chatgpt.com",
  "claude.ai",
  "copilot.com",
  "copilot.microsoft.com",
  "deepseek.com",
  "edgeservices.bing.com",
  "gemini.google.com",
  "meta.ai",
  "openai.com",
  "perplexity.ai",
  "phind.com",
  "poe.com",
  "you.com",
])

const SEARCH_REFERRER_HOSTS = new Set([
  "baidu.com",
  "bing.com",
  "duckduckgo.com",
  "ecosia.org",
  "google.com",
  "search.brave.com",
  "search.yahoo.com",
  "yahoo.com",
  "yandex.com",
  "yandex.ru",
])

/**
 * Pick UTM query params from a URLSearchParams-like object.
 */
export function utmParamsFromSearchParams(
  params: URLSearchParams | { get: (name: string) => string | null },
): WsaAttributionPayload {
  const out: WsaAttributionPayload = {}
  for (const key of UTM_KEYS) {
    const v = params.get(key)
    if (v?.trim()) out[key] = v.trim().slice(0, MAX_UTM_VALUE_LENGTH)
  }
  return out
}

export function hasAnyUtm(payload: WsaAttributionPayload): boolean {
  return UTM_KEYS.some(k => Boolean(payload[k]?.trim()))
}

export function classifyMarketingChannel({
  utmMedium,
  utmSource,
  utmCampaign,
  referrer,
}: MarketingChannelInput): MarketingChannelGroup {
  const medium = normalizeToken(utmMedium)
  const source = normalizeHostLikeToken(utmSource)
  const referrerUrl = parseReferrerUrl(referrer)
  const referrerHost = getUrlHost(referrerUrl)

  if (
    medium === "ai_referral" ||
    isAiHost(source) ||
    isAiReferrer(referrerHost, referrerUrl?.pathname)
  ) {
    return "ai_referral"
  }

  if (medium === "organic") {
    return "organic_search"
  }

  // Explicit campaign tagging beats a search-engine referrer: paid search
  // (e.g. utm_medium=cpc with a google.com referrer) is paid, not organic.
  if (medium || source || normalizeToken(utmCampaign)) {
    return "paid_or_campaign"
  }

  if (isSearchReferrer(referrerHost)) {
    return "organic_search"
  }

  if (referrerHost) {
    return "referral"
  }

  return "direct"
}

export function getReferrerHost(referrer: string | undefined | null): string | null {
  return getUrlHost(parseReferrerUrl(referrer))
}

/**
 * Serialise attribution for Set-Cookie (URL-encoded JSON — safe inside Cookie header).
 */
export function serializeWsaAttribution(payload: WsaAttributionPayload): string {
  return encodeURIComponent(JSON.stringify(payload))
}

/**
 * Parse `wsa_attribution` cookie value. Returns `{}` on empty/invalid input.
 */
export function parseWsaAttributionCookie(raw: string | undefined | null): WsaAttributionPayload {
  if (!raw?.trim()) return {}
  try {
    const decoded = safeDecodeURIComponent(raw.trim())
    const parsed = JSON.parse(decoded) as unknown
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
    const o = parsed as Record<string, unknown>
    const out: WsaAttributionPayload = {}
    for (const key of UTM_KEYS) {
      const v = o[key]
      if (typeof v === "string" && v.trim()) out[key] = v.trim().slice(0, MAX_UTM_VALUE_LENGTH)
    }
    return out
  } catch {
    return {}
  }
}

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/**
 * Merge URL UTMs with cookie first-touch: URL wins per-key when present, else cookie.
 */
export function mergeUtmForEvent(
  fromUrl: WsaAttributionPayload,
  fromCookie: WsaAttributionPayload,
): WsaAttributionPayload {
  const out: WsaAttributionPayload = { ...fromCookie }
  for (const key of UTM_KEYS) {
    const u = fromUrl[key]
    if (u) out[key] = u.slice(0, MAX_UTM_VALUE_LENGTH)
  }
  return out
}

function isAiReferrer(host: string | null, pathname: string | undefined): boolean {
  if (!host) return false
  if (isAiHost(host)) return true
  return host === "bing.com" && Boolean(pathname === "/chat" || pathname?.startsWith("/chat/"))
}

function isAiHost(host: string | null): boolean {
  return Boolean(host && AI_REFERRER_HOSTS.has(host))
}

function isSearchReferrer(host: string | null): boolean {
  if (!host) return false
  if (SEARCH_REFERRER_HOSTS.has(host)) return true
  return isGoogleSearchHost(host) || host.endsWith(".bing.com")
}

function isGoogleSearchHost(host: string): boolean {
  return host.endsWith(".google.com") || /^google\.[a-z]{2,3}(?:\.[a-z]{2})?$/.test(host)
}

function parseReferrerUrl(referrer: string | undefined | null): URL | null {
  if (!referrer?.trim()) return null
  try {
    return new URL(referrer)
  } catch {
    return null
  }
}

function getUrlHost(url: URL | null): string | null {
  if (!url) return null
  const host = normalizeHost(url.hostname)
  return host || null
}

function normalizeToken(value: string | undefined | null): string | null {
  const normalized = value?.trim().toLowerCase()
  return normalized || null
}

function normalizeHostLikeToken(value: string | undefined | null): string | null {
  const token = normalizeToken(value)
  if (!token) return null

  const withoutProtocol = token.replace(/^https?:\/\//, "")
  // Drop any path, query, fragment, or port so a full-URL utm_source like
  // `https://perplexity.ai?x=1` still resolves to the bare `perplexity.ai` host.
  const host = withoutProtocol.split(/[/?#:]/)[0]
  return host ? normalizeHost(host) : null
}

function normalizeHost(host: string): string {
  return host
    .trim()
    .toLowerCase()
    .replace(/^www\./, "")
}

/**
 * Read `wsa_attribution` from `document.cookie` (browser only).
 */
export function readWsaAttributionFromDocumentCookie(): WsaAttributionPayload {
  if (typeof document === "undefined" || !document.cookie) return {}
  const prefix = `${WSA_ATTRIBUTION_COOKIE}=`
  for (const part of document.cookie.split(";")) {
    const p = part.trim()
    if (p.startsWith(prefix)) {
      return parseWsaAttributionCookie(p.slice(prefix.length))
    }
  }
  return {}
}
